import { CUSTOM_STYLE_MARKER } from './stripTemplateContent';

// ─── Styles ──────────────────────────────────────────────────────────────────

export const EMAIL_STYLES = `
body {
  margin: 0;
  padding: 0;
  background-color: #ffffff;
  font-family: 'Inter', Arial, sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #192636;
}
.email-container {
  max-width: 688px;
  margin: 0 auto;
  background-color: #ffffff;
}

[data-template-header] {
  box-sizing: border-box;
  padding: 24px 24px 0 24px;
  text-align: center;
}

.email-body {
  box-sizing: border-box;
  padding: 0 24px 24px 24px;
  font-family: 'Inter', Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #192636;
  width: 100%;
  overflow-wrap: anywhere;
}
.email-body > *:first-child { margin-top: 0; }
.email-body > *:last-child { margin-bottom: 0; }

/* Keep content from outgrowing its container: an auto-layout table or an oversized image would
   otherwise overflow. Inline widths still win, so migrated layouts keep their own sizing. */
.email-body img { max-width: 100%; }
.email-body table { max-width: 100%; }

/* TinyMCE applies this padding in its editor content CSS only, so a saved table would lose 0.4rem
   around every cell. Its own selector: a migrated MJML table carries cellpadding="0" and is skipped. */
.email-body table:not([cellpadding]) th,
.email-body table:not([cellpadding]) td { padding: 0.4rem; }

/* Cells saved as <td></td> before the editor started padding empty ones (EMAIL_EXTENDED_VALID_ELEMENTS)
   have no line box. Browser-only — Outlook ignores generated content. */
.email-body table:not([cellpadding]) th:empty::after,
.email-body table:not([cellpadding]) td:empty::after { content: "\\00a0"; }

[data-template-disclosures] {
  box-sizing: border-box;
  padding: 24px;
  font-family: 'Inter', Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #8c929a;
  width: 100%;
}
[data-disclosure-id] + [data-disclosure-id] { margin-top: 20px; }
[data-disclosure-id] p {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #8c929a;
  overflow-wrap: anywhere;
}

[data-template-sender] {
  box-sizing: border-box;
  padding: 24px;
  text-align: center;
  font-family: 'Inter', Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #192636;
  width: 100%;
}
[data-template-sender] p {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #192636;
  overflow-wrap: anywhere;
}
[data-template-sender] a,
[data-template-sender] span {
  color: #231596;
  text-decoration: underline;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
`;

// Preview-only. The panel frames the body with its own gutter and puts disclosures/sender at that
// inset, so the shell's container and padding would push the body text further in than them. Cost:
// the preview's text column is wider than the 688px one a recipient gets.
export const PREVIEW_STYLES = `
.email-container { max-width: none; }
.email-body { padding-left: 0; padding-right: 0; }
`;

// ─── Tag builders ─────────────────────────────────────────────────────────────

const logoTag = (src) =>
  `<img src="${src}" alt="Waythru" style="${[
    'display:block',
    'height:40px',
    'width:174px',
    'object-fit:contain',
    'object-position:center',
    'margin:0 auto',
    'border:0',
  ].join(';')}">`;

const headerTag = (content) => `<div data-template-header="true">${content}</div>`;

const disclosureItemTag = (d) =>
  `<div data-disclosure-id="${String(d.id)}"><p>{{${String(d.name ?? '')}}}</p></div>`;

const disclosuresTag = (content) => `<div data-template-disclosures="true">${content}</div>`;

const senderTag = (content) => `<div data-template-sender="true">${content}</div>`;

// ─── Section builders ─────────────────────────────────────────────────────────

export function buildEmailHeaderHtml(logoUrl) {
  const logo = logoUrl ? logoTag(logoUrl) : '';
  if (!logo) return '';
  return headerTag(logo);
}

export function buildDisclosureHtml(disclosures) {
  if (!Array.isArray(disclosures) || disclosures.length === 0) return '';
  return disclosuresTag(disclosures.map(disclosureItemTag).join(''));
}

export function buildSenderHtml(senderInfo) {
  if (!senderInfo) return '';

  const parts = [];

  if (senderInfo.name) parts.push(`<p>${senderInfo.name}</p>`);

  const addressLine = [
    senderInfo.address,
    senderInfo.city,
    [senderInfo.state, senderInfo.zip].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  if (addressLine) parts.push(`<p>${addressLine}</p>`);

  if (senderInfo.unsubscribePage) {
    parts.push(`<a href="${senderInfo.unsubscribePage}">Unsubscribe</a>`);
  } else {
    parts.push(`<span>Unsubscribe</span>`);
  }

  if (parts.length === 0) return '';
  return senderTag(parts.join(''));
}

// ─── Shell ────────────────────────────────────────────────────────────────────

export function buildEmailShell(
  header,
  body,
  disclosures,
  sender,
  customStyle = '',
  extraStyles = '',
) {
  // A template's own responsive/custom CSS (e.g. @media blocks from migrated MJML) is
  // re-attached in a separate, marked <style> after the shell styles so it survives the
  // extract→rebuild round-trip. The marker lets extractCustomStyle find it again next time.
  const custom = String(customStyle ?? '').trim();
  const customStyleTag = custom ? `\n<style ${CUSTOM_STYLE_MARKER}>${custom}</style>` : '';
  // Unmarked and last: outranks the blocks above, never round-trips as the template's own CSS.
  const extra = String(extraStyles ?? '').trim();
  const extraStyleTag = extra ? `\n<style>${extra}</style>` : '';
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap" rel="stylesheet">
<style>${EMAIL_STYLES}</style>${customStyleTag}${extraStyleTag}
</head>
<body>
<div class="email-container">
${header}
<div class="email-body">${body}</div>
${disclosures}
${sender}
</div>
</body>
</html>`;
}

// Full HTML document for the preview <iframe>: delegates to buildEmailShell so preview and
// saved output share one shell. Header/disclosures/sender are empty — the panel renders those
// as its own chrome; only the body goes inside.
export function buildPreviewDocument(body, customStyle = '') {
  return buildEmailShell('', String(body ?? ''), '', '', customStyle, PREVIEW_STYLES);
}
